import type { PathwayCourseKey } from "./pathway-course-ids";

export const PATHWAY_PACKAGE_VARIANTS = [
  {
    id: "full",
    title: "Full Access to Level 7",
    audience: "No experience or no regulated qualifications",
    description: "Start with Level 3 Access, then progress through Levels 4, 5 and 7, with BLS included.",
    priceIncVatPence: 1_299_880,
    courseKeys: ["access", "bls", "level4", "level5", "level7Diploma"],
  },
  {
    id: "level4",
    title: "Level 4–7 with BLS",
    audience: "Entering at Level 4",
    description: "For learners with the entry qualifications to start Level 4. Includes Levels 4, 5 and 7, plus BLS.",
    priceIncVatPence: 1_199_880,
    courseKeys: ["bls", "level4", "level5", "level7Diploma"],
  },
  {
    id: "level5",
    title: "Level 5–7 with BLS",
    audience: "Entering at Level 5",
    description: "For learners with the entry qualifications to start Level 5. Includes Levels 5 and 7, plus BLS.",
    priceIncVatPence: 899_900,
    courseKeys: ["bls", "level5", "level7Diploma"],
  },
] as const satisfies ReadonlyArray<{
  id: string;
  title: string;
  audience: string;
  description: string;
  priceIncVatPence: number;
  courseKeys: readonly PathwayCourseKey[];
}>;

export type PathwayPackageEntry = typeof PATHWAY_PACKAGE_VARIANTS[number]["id"];
export type PathwayPackageVariant = typeof PATHWAY_PACKAGE_VARIANTS[number];

export function pathwayPackageSeparateTotal(
  variant: PathwayPackageVariant,
  courses: Array<{ key: PathwayCourseKey; standardPriceIncVatPence: number | null }>,
): number | null {
  const prices = variant.courseKeys.map(key => courses.find(course => course.key === key)?.standardPriceIncVatPence);
  return prices.every(price => typeof price === "number" && Number.isFinite(price))
    ? prices.reduce<number>((sum, price) => sum + price!, 0)
    : null;
}

export function isPathwayPackageEntry(value: string | null): value is PathwayPackageEntry {
  return PATHWAY_PACKAGE_VARIANTS.some(variant => variant.id === value);
}

export function pathwayPackageFromParams(params: URLSearchParams | null): PathwayPackageVariant | null {
  if (params?.get("source") !== "course_pathway") return null;
  return PATHWAY_PACKAGE_VARIANTS.find(variant => variant.id === params.get("pathway_entry")) ?? null;
}

/** Only the existing Level 4–7 product has a matching online checkout configuration. */
export function canBookPathwayPackageOnline(entry: PathwayPackageEntry, bookingPriceExVatPence: number | null) {
  if (entry !== "level4" || bookingPriceExVatPence == null) return false;
  const variant = PATHWAY_PACKAGE_VARIANTS.find(option => option.id === entry)!;
  return bookingPriceExVatPence + Math.round(bookingPriceExVatPence * 0.2) === variant.priceIncVatPence;
}
